import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import themeTokens from '@/config/theme-tokens.js';
import { color } from '@/constants/index';
import { invertColorByBrightness } from '@/utils/colorConverter';

export const CheckModal = ({
  title,
  description,
  backgroundColor,
  executeButtonName = '実行',
  cancelButtonClassName,
  executeButtonClassName,
  executeButtonContent,
  handleExecute,
  handleCancel,
}: {
  title: string;
  description?: string | null;
  backgroundColor: string;
  executeButtonName?: string;
  cancelButtonClassName?: string;
  executeButtonClassName?: string;
  executeButtonContent?: ReactNode;
  handleExecute?: () => void;
  handleCancel?: () => void;
}) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const { color: themeColor } = themeTokens;

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[120] flex w-full items-center justify-center bg-black/60 p-5"
      style={{
        height: '100vh',
      }}
    >
      <div className="mx-auto w-full md:max-w-drawer-max">
        <div
          className="rounded-lg px-4 py-6"
          style={{
            backgroundColor: backgroundColor ?? themeColor.gray.surface,
          }}
          data-testid="check-modal"
        >
          <div className="mb-6 flex items-center justify-center rounded-lg text-xl font-semibold">
            <span
              className="currentColor text-justify"
              style={{ color: invertColorByBrightness(backgroundColor) }}
            >
              {title}
            </span>
          </div>

          <div
            className="hidden-scrollbar relative mb-6 max-h-[40vh] overflow-scroll whitespace-pre-wrap break-words rounded-lg bg-white p-[15px] text-justify text-sm"
            data-testid="check-modal-description-textbox"
          >
            {description}
          </div>
          <div className="mb-1 flex items-center justify-end gap-2">
            {handleCancel !== undefined && (
              <button
                className={
                  'min-w-[110px] rounded-lg border bg-white px-4 py-2 shadow-sm duration-200 hover:bg-hover-white hover:shadow ' +
                  (cancelButtonClassName ?? '')
                }
                style={{
                  borderColor: themeColor.gray.border,
                  color: themeColor.gray.muted,
                }}
                onClick={() => handleCancel()}
                aria-label="キャンセル"
                data-testid="check-cancel-button"
              >
                <p className="ml-px text-[14px] font-semibold">キャンセル</p>
              </button>
            )}
            {handleExecute !== undefined && (
              <button
                className={
                  'min-w-[140px] rounded-lg border bg-white px-4 py-2 shadow-sm duration-200 hover:bg-hover-white hover:shadow ' +
                  (executeButtonClassName ?? '')
                }
                style={{
                  borderColor: themeColor.gray.border,
                  color: color.activeColor,
                }}
                onClick={() => handleExecute()}
                aria-label={executeButtonName}
                data-testid="check-execution-button"
              >
                {executeButtonContent ?? (
                  <p className="ml-px text-[14px] font-semibold">
                    {executeButtonName}
                  </p>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
